import { addDoc, collection } from "firebase/firestore";
import { waitForCheckoutSession } from "./checkout-session";
import {
  getCurrentUserPayments,
  getCurrentUserSubscriptions,
  getProducts,
  getStripePayments,
  type Payment,
  type Subscription,
} from "@invertase/firestore-stripe-payments";
import { httpsCallable } from "firebase/functions";
import {
  buildBillingCatalog,
  deriveCurrentPlanIdFromStripeSubscriptions,
  stripeExtensionCollections,
  type BillingCatalogOffer,
  type StripeCatalogProductLike,
  type PlanId,
} from "@reelmagic/domain";
import {
  assertFirebaseMutationAllowed,
  getFirebaseClientServices,
  isFirebaseWebConfigReady,
} from "../firebase/client";

function readCollectionName(
  envValue: string | undefined,
  fallback: string,
): string {
  return envValue?.trim() ? envValue : fallback;
}

export function getStripeExtensionConfig() {
  return {
    customersCollection: readCollectionName(
      process.env.NEXT_PUBLIC_STRIPE_EXTENSION_CUSTOMERS_COLLECTION,
      stripeExtensionCollections.customers,
    ),
    productsCollection: readCollectionName(
      process.env.NEXT_PUBLIC_STRIPE_EXTENSION_PRODUCTS_COLLECTION,
      stripeExtensionCollections.products,
    ),
    instanceId: process.env.NEXT_PUBLIC_STRIPE_EXTENSION_INSTANCE_ID ?? "",
  };
}

export function isStripeExtensionReady() {
  if (!isFirebaseWebConfigReady()) {
    return false;
  }

  const config = getStripeExtensionConfig();

  return Boolean(config.customersCollection && config.productsCollection);
}

export function getStripeExtensionClient() {
  const { app } = getFirebaseClientServices();
  const config = getStripeExtensionConfig();

  return getStripePayments(app, {
    customersCollection: config.customersCollection,
    productsCollection: config.productsCollection,
  });
}

async function assertBillingMutationAllowed() {
  assertFirebaseMutationAllowed();
  const { auth } = getFirebaseClientServices();
  const token = await auth.currentUser?.getIdTokenResult();
  if (token?.claims.impersonating === true) {
    throw new Error("Billing changes are unavailable while viewing the app as another user.");
  }
}

export async function getBillingCatalog() {
  const payments = getStripeExtensionClient();
  const products = await getProducts(payments, {
    includePrices: true,
    activeOnly: true,
  });

  return {
    offers: buildBillingCatalog(products as StripeCatalogProductLike[]),
    products,
  };
}

export async function startStripeExtensionCheckout({
  cancelUrl,
  mode,
  offerKey,
  priceId,
  successUrl,
}: {
  cancelUrl: string;
  mode: "payment" | "subscription";
  offerKey: string;
  priceId: string;
  successUrl: string;
}) {
  await assertBillingMutationAllowed();
  const { auth, firestore } = getFirebaseClientServices();
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Sign in before you select a plan.");
  if (!priceId.trim() || !offerKey.trim() || !successUrl.trim() || !cancelUrl.trim()) {
    throw new Error("Checkout requires a price, offer, success URL, and cancel URL.");
  }
  const config = getStripeExtensionConfig();
  const metadata: Record<string, string> = {
    reelmagicOfferKey: offerKey,
  };

  const checkoutParams = {
    cancel_url: cancelUrl,
    allow_promotion_codes: true,
    metadata,
    mode,
    price: priceId,
    success_url: successUrl,
  };
  const document = await addDoc(
    collection(firestore, config.customersCollection, userId, "checkout_sessions"),
    checkoutParams,
  );
  return waitForCheckoutSession(document);
}

export async function getCurrentStripeBillingState(): Promise<{
  payments: Payment[];
  subscriptions: Subscription[];
}> {
  const payments = getStripeExtensionClient();

  const [currentPayments, currentSubscriptions] = await Promise.all([
    getCurrentUserPayments(payments),
    getCurrentUserSubscriptions(payments),
  ]);

  return {
    payments: currentPayments,
    subscriptions: currentSubscriptions,
  };
}

export function deriveCurrentPlanIdFromBillingState(subscriptions: Subscription[]): PlanId {
  return deriveCurrentPlanIdFromStripeSubscriptions(subscriptions);
}

export async function createCustomerPortalLink(returnUrl: string) {
  await assertBillingMutationAllowed();
  const { functions } = getFirebaseClientServices();
  const config = getStripeExtensionConfig();
  const callableName = config.instanceId
    ? `ext-${config.instanceId}-createPortalLink`
    : "";

  if (!callableName) {
    throw new Error("Stripe extension portal function is not configured.");
  }

  const callable = httpsCallable<
    { returnUrl?: string },
    { url?: string; portalLink?: string }
  >(functions, callableName);
  const response = await callable({ returnUrl });

  return response.data.url ?? response.data.portalLink ?? null;
}

export function getBillingSetupChecklist() {
  return {
    firebaseReady: isFirebaseWebConfigReady(),
    stripeExtensionConfigured: isStripeExtensionReady(),
  };
}

export type { BillingCatalogOffer };
